import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, has, set } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import CalendarDownIcon from '../../images/calendar-arrow-down.png';
import CalendarUpIcon from '../../images/calendar-arrow-up.png';
import MapsIcon from '../../images/maps-and-flags.png';
import ReverseIcon from '../../images/rotate-reverse.png';
import UserIcon from '../../images/user.png';
import cart from '../../images/shopping-cart.png';
import ClientButton from '../../components/ClientButton';
import LeftArrow from '../../components/Icons/LeftArrow';
import { toast } from 'react-hot-toast';
import OrderConfirmation from './OrderConfirmation';
import OrderSummary from './OrderSummary';
import PaymentMethod from './PaymentMethod';
import PickupAddress from './PickupAddress';
import PickupAndDelivery from './PickupAndDelivery';
import SelectProvider from './SelectProvider';
import SelectServices from './SelectServices';
import OrderType from './OrderType';
import { setOrderDataAction, setOrderTypeAction } from '../../redux/Actions/CreateOrderActions';
import { ArrowLongLeftIcon, ArrowLongRightIcon } from '@heroicons/react/20/solid';
import RightArrow from '../../components/Icons/RightArrow';
import { resetCreateOrderFlow } from '../../redux/Reducers/CreateOrderReducer';
import { dateFn } from '../../utils/dateTimeFormatter';
import ModalWithHeading from '../../components/ModalWithHeading';
import ServicesEstimate from './OrderSummary/ServicesEstimate';
import ServiceEstimateModal from './ServiceEstimateModal';

export default function CreateNewOrder() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    orderType,
    orderServiceAddress,
    selectedServices,
    orderTimeSlots,
    orderRepeatFrequency,
    orderProvider,
    orderPaymentDetails,
    orderPreferences,
  } = useSelector((state) => state.createNewOrder);
  const { profileData } = useSelector((state) => state.profile);
  const [selected, setSelected] = useState(1);
  const [count, setCount] = useState(1800);
  const [serviceEstimateModal, setServiceEstimateModal] = useState(false);
  const changeStepTitle = (val) => {
    switch (val) {
      case 1:
        return 'Create New Order';
      case 2:
        return 'Where should we pick up your laundry?';
      case 3:
        return 'Select Services';
      case 4:
        return 'Select Provider & Pickup';
      case 5:
        return 'Select Delivery';
      case 6:
        return 'Confirm Payment';
      case 7:
        return 'Confirm Order';
      default:
        return '';
    }
  };

  const orderCreationValidation = (val) => {
    switch (val) {
      case 1:
        return !orderType;
      case 2:
        return !orderServiceAddress;
      case 3:
        return orderServicesValidationFn();
      case 4:
        return !orderTimeSlots && !orderProvider;
      case 5:
        return orderPickupDeliveryValidationFn(orderTimeSlots);
      case 6:
        return orderPaymentDetailsValidationFn();
      case 7:
        return 'Confirm Order';
      default:
        return '';
    }
  };
  const pickupDeliveryInstructionCheckFn = () => {
    const pd = String(orderPreferences?.pickup_delivery || '').toLowerCase();
    if (pd !== 'other') return false;
    const instr = String(orderPreferences?.pickup_delivery_instructions || '').trim();
    return pd !== 'other' || instr.length < 5;
  };
  const orderServicesValidationFn = () => {
    if (isEmpty(selectedServices)) {
      return true;
    } else {
      return pickupDeliveryInstructionCheckFn();
    }
  };
  const orderPickupDeliveryValidationFn = (slots) => {
    if (orderType === 'oneTime') {
      return !(
        slots?.pickup_date &&
        slots?.pickup_start_time &&
        slots?.pickup_end_time &&
        slots?.dropoff_date &&
        slots?.dropoff_start_time &&
        slots?.dropoff_end_time
      );
    } else if (orderType === 'subscription') {
      return !(
        slots?.first_pickup_date &&
        slots?.pickup_start_time &&
        slots?.pickup_end_time &&
        slots?.dropoff_date &&
        slots?.dropoff_start_time &&
        slots?.dropoff_end_time &&
        orderRepeatFrequency
      );
    }
  };

  const orderPaymentDetailsValidationFn = () => {
    let type = orderPaymentDetails?.billing_details?.default_payment_method_type;
    if (type == 'ACH' || type == 'CHECK') {
      return false;
    } else if (type == 'STRIPE' && orderPaymentDetails?.billing_details?.default_payment_method) {
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    if (profileData?.status !== 'ACTIVE') {
      toast.error('Your account is inactive!');
      navigate('/dashboard');
    } else if (!profileData?.is_allowed_to_create_order) {
      toast.error('Oops! You have a past due invoice. Make a payment to order.');
      navigate('/account', { state: { from: 'payments' } });
    }
  }, [profileData?.status]);

  useEffect(() => {
    selected == 1 && dispatch(resetCreateOrderFlow());
    if (selected == 4) {
      setCount(1800);
    } else if (selected == 8) {
      setCount(1800);
    }
  }, [selected]);

  // Timer that tracks remaining seconds using Date.now() so it continues while the tab is inactive/minimized.
  const timerIntervalRef = useRef(null);
  const expiryRef = useRef(null);

  useEffect(() => {
    // Cleanup helper
    const clearTimer = () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      expiryRef.current = null;
    };

    // Start timer only when orderTimeSlots exists and user is on step 4
    if (!isEmpty(orderTimeSlots) && (selected == 4 || selected == 5 || selected == 6 || selected == 7)) {
      // If we don't already have an expiry, set one to now + 1800s
      if (!expiryRef.current) {
        expiryRef.current = Date.now() + 1800 * 1000;
      }

      // Immediately compute remaining and set
      const computeRemaining = () => Math.max(0, Math.round((expiryRef.current - Date.now()) / 1000));
      setCount(computeRemaining());

      // If there's an existing interval, clear it first
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      timerIntervalRef.current = setInterval(() => {
        const remaining = computeRemaining();
        setCount(remaining);
        if (remaining <= 0) {
          clearTimer();
        }
      }, 1000);
    } else {
      // If orderTimeSlots is empty or user navigated away from step 4, clear timer
      clearTimer();
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [orderTimeSlots, selected]);
  useEffect(() => {
    if (count === 300) {
      toast.error(
        `You've been inactive for a while. Your session will expire soon. Please complete your order to avoid losing your progress.`
      );
    } else if (count === 0) {
      setSelected(1);
      toast.error(`Your session expired due to inactivity. Please start a new order.`);
      setCount(1800);
    } else if (selected == 1 || selected == 2 || selected == 3) {
      setCount(1800);
    }
  }, [count]);
  return (
    <div className=''>
      <div className=' bg-white '>
        <div className='sm:max-w-9xl px-4 py-4 sm:mx-auto sm:px-6 '>
          <div className='flex flex-col items-center justify-center space-y-3 py-2 sm:py-4 '>
            <h3 className='go-textblackbold-18 sm:go-textblackbold-24'>{changeStepTitle(selected)}</h3>
            {selected > 2 && selected < 8 && (
              <div className='absolute right-5 top-5 block sm:hidden '>
                <img
                  src={cart}
                  className='bg-client-300 h-10 w-10 rounded-full p-1.5'
                  alt='cart'
                  onClick={(e) => {
                    e.stopPropagation();
                    setServiceEstimateModal(true);
                  }}
                />
                <div className=' absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 align-middle text-white'>
                  <span className='text-sm'> {selectedServices?.length}</span>
                </div>
              </div>
            )}
            <div className='w-full flex-col space-y-2 align-middle sm:w-96'>
              {(selected == 2 || selected == 3 || selected == 4 || selected == 5 || selected == 6) && (
                <div
                  className='text-client-primary go-textblackbold-14 flex cursor-pointer font-semibold'
                  onClick={() => setSelected(1)}
                >
                  <img className='mr-2 h-5 w-5' src={ReverseIcon} alt='Order type' />
                  {orderType == 'subscription' && orderRepeatFrequency?.value && (
                    <span>{orderRepeatFrequency?.name}</span>
                  )}
                  {orderType == 'oneTime' && <span>One-time</span>}
                </div>
              )}
              {orderServiceAddress?.full_address &&
                (selected == 2 || selected == 3 || selected == 4 || selected == 5 || selected == 6) && (
                  <div
                    className='text-client-primary go-textblackbold-14 flex cursor-pointer  font-semibold'
                    onClick={() => setSelected(2)}
                  >
                    <img className='mr-2 h-5 w-5' src={MapsIcon} alt='address' />
                    <span>{orderServiceAddress?.full_address}</span>
                  </div>
                )}
              {(selected == 4 || selected == 5 || selected == 6) &&
                (orderProvider == null ? (
                  <div
                    className='text-client-primary go-textblackbold-14 flex cursor-pointer  font-semibold'
                    onClick={() => setSelected(4)}
                  >
                    <img className='mr-2 h-5 w-5' src={UserIcon} alt='provider' />
                    <span>Provider - Auto Match</span>
                  </div>
                ) : (
                  <div
                    className='text-client-primary go-textblackbold-14 flex cursor-pointer  font-semibold'
                    onClick={() => setSelected(4)}
                  >
                    <img className='mr-2 h-5 w-5' src={UserIcon} alt='provider' />
                    <span>
                      Provider - {orderProvider?.first_name} {orderProvider?.last_name}
                    </span>
                  </div>
                ))}{' '}
              {(orderTimeSlots?.pickup_date || orderTimeSlots?.pickup_start_time) &&
                (selected == 4 || selected == 5 || selected == 6) && (
                  <div
                    className='text-client-primary go-textblackbold-14 flex cursor-pointer  font-semibold'
                    onClick={() => setSelected(4)}
                  >
                    <img className='mr-2 h-5 w-5' src={CalendarUpIcon} alt='pickup time' />
                    {orderType == 'oneTime' ? (
                      <span>
                        {dateFn(
                          'toLocal',
                          orderTimeSlots?.pickup_date,
                          orderTimeSlots?.pickup_start_time,
                          orderTimeSlots?.pickup_end_time,
                          orderServiceAddress?.time_zone
                        )}
                      </span>
                    ) : (
                      <span>
                        {dateFn(
                          'toLocal',
                          orderTimeSlots?.first_pickup_date,
                          orderTimeSlots?.pickup_start_time,
                          orderTimeSlots?.pickup_end_time,
                          orderServiceAddress?.time_zone
                        )}
                      </span>
                    )}
                  </div>
                )}
              {orderTimeSlots?.dropoff_date && (selected == 5 || selected == 6) && (
                <div
                  className='text-client-primary go-textblackbold-14 flex cursor-pointer  font-semibold'
                  onClick={() => setSelected(5)}
                >
                  <img className='mr-2 h-5 w-5' src={CalendarDownIcon} alt='dropoff time' />
                  {dateFn(
                    'toLocal',
                    orderTimeSlots?.dropoff_date,
                    orderTimeSlots?.dropoff_start_time,
                    orderTimeSlots?.dropoff_end_time,
                    orderServiceAddress?.time_zone
                  )}
                </div>
              )}
            </div>
          </div>
          <div className='flex items-center justify-between py-2 sm:mb-4 sm:py-0'>
            {selected > 1 && selected !== 8 ? (
              <span>
                <button
                  className={`btn bg-client-text cursor-pointer rounded-full text-white `}
                  onClick={() => setSelected(selected - 1)}
                >
                  <ArrowLongLeftIcon onClick={() => setSelected(selected - 1)} className={`h-6 w-6 sm:w-10`} />
                  <span className='ml-2 mr-0 sm:ml-0 sm:mr-2'>Back</span>
                </button>
              </span>
            ) : (
              <span className='w-24'></span>
            )}

            {selected > 0 && selected !== 7 && selected !== 8 ? (
              <span>
                <button
                  className={`btn bg-client-text cursor-pointer rounded-full text-white ${
                    orderCreationValidation(selected)
                      ? 'pointer-events-none opacity-50'
                      : 'pointer-events-auto opacity-100'
                  }`}
                  onClick={() => setSelected(selected + 1)}
                >
                  <span className='ml-0 mr-2 sm:ml-2 sm:mr-0'>Next</span>
                  <ArrowLongRightIcon className={`h-6 w-6 sm:w-10`} />
                </button>
              </span>
            ) : (
              <span className='w-24'></span>
            )}
          </div>
        </div>
      </div>
      <ServiceEstimateModal
        serviceEstimateModal={serviceEstimateModal}
        setServiceEstimateModal={setServiceEstimateModal}
      />
      <div className='sm:max-w-9xl w-full p-4 sm:mx-auto sm:p-6'>
        <div className='w-full'>
          {selected === 1 && (
            <div className='relative'>
              <OrderType />
            </div>
          )}
          {selected === 2 && (
            <div className='relative'>
              <PickupAddress />
            </div>
          )}
          {selected === 3 && (
            <div className='relative'>
              <SelectServices />
            </div>
          )}
          {selected === 4 && (
            <div className='relative'>
              <SelectProvider setstep={() => setSelected(5)} />
            </div>
          )}
          {selected === 5 && (
            <div className='relative'>
              <PickupAndDelivery />
            </div>
          )}
          {selected === 6 && (
            <div className='relative'>
              <PaymentMethod />
            </div>
          )}
          {selected === 7 && (
            <div className='relative'>
              <OrderSummary setstep={() => setSelected(8)} />
            </div>
          )}
          {selected === 8 && (
            <div className='relative'>
              <OrderConfirmation />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
